import { resolveIconHref } from "../../icons/resolve-icon.js";
import { cn, tv } from "tailwind-variants";
import { Heading } from "#/_packages/umbrella/components/ui/primitives/heading.tsx";
import { useAnimationEffect } from "#/_packages/umbrella/integrations/umbraco/hooks/use-animation-effect.ts";
import {
	toHoverItemCssClasses,
	toHoverMediaCssClasses,
} from "#/_packages/umbrella/integrations/umbraco/utils/animation-utils.ts";
import type {
	Alignment,
	AnimationEffectType,
	AnimationScrollType,
	HeadingLevel,
	Intent,
	Link,
	Orientation,
	ReadWidthLength,
	Variant,
} from "#/_packages/umbrella/types";
import { getAltTextFromString } from "#/_packages/umbrella/utils/image-utils.ts";
import { getLinkAttributes } from "#/_packages/umbrella/utils/link-attributes";

function toAspectRatioValue(aspectRatio?: string | null): string | undefined {
	if (!aspectRatio) return undefined;
	const normalized = aspectRatio.replace(":", "-").trim();
	const parts = normalized.split("-").map((x) => Number(x));
	if (
		parts.length !== 2 ||
		parts.some((value) => Number.isNaN(value) || value <= 0)
	) {
		return undefined;
	}

	return `${parts[0]} / ${parts[1]}`;
}

function toReadWidthLengthClass(readWidthLength: ReadWidthLength): string {
	switch (readWidthLength) {
		case "narrow":
			return "read-width-narrow";
		case "wide":
			return "read-width-wide";
		default:
			return "read-width-normal";
	}
}

function toReadWidthAlignmentClass(alignment: Alignment): string {
	switch (alignment) {
		case "start":
			return "read-width-start";
		case "end":
			return "read-width-end";
		default:
			return "read-width-center";
	}
}

function toTeaserAlignment(
	alignment: Alignment,
): Exclude<Alignment, "stretch"> {
	return alignment === "stretch" ? "start" : alignment;
}

export type TeaserProps = {
	inlineEditing?: boolean;
	inlineElementKey?: string;
	orientation?: Orientation;
	heading?: string | null;
	headingLabel?: string | null;
	headingLevel?: HeadingLevel;
	headingAppearance?: HeadingLevel;
	body?: string;
	imageSrc?: string | null;
	imageAlt?: string | null;
	imageCaption?: string | null;
	imageInBackground?: boolean;
	imageInset?: boolean;
	imagePositionEnd?: boolean;
	iconSvgId?: string | null;
	iconColor?: string | null;
	iconPositionTop?: boolean;
	iconAlignment?: Alignment;
	link?: Link;
	intentColor?: Intent;
	typeVariant?: Variant;
	contentAlignment?: Alignment;
	animationHoverType?: string[];
	isDrawer?: boolean;
	animationScrollType?: AnimationScrollType;
	animationEffectType?: AnimationEffectType | string;
	readWidthToggle?: boolean;
	readWidthLength?: ReadWidthLength;
	readWidthAlignment?: Alignment;
	aspectRatio?: string | null;
	id?: string;
	className?: string;
};

const teaser = tv({
	slots: {
		base: "block__teaser h-full",
	},
	variants: {
		iconAlignment: {
			start: { base: "teaser-icon-align-start" },
			center: { base: "teaser-icon-align-center" },
			end: { base: "teaser-icon-align-end" },
		},
		contentAlignment: {
			start: { base: "content-alignment-start" },
			center: { base: "content-alignment-center" },
			end: { base: "content-alignment-end" },
		},
		variant: {
			solid: { base: "teaser-variant-solid" },
			outline: { base: "teaser-variant-outline" },
			ghost: { base: "teaser-variant-ghost" },
			soft: { base: "teaser-variant-soft" },
			quiet: { base: "teaser-variant-quiet" },
		},
		intent: {
			primary: { base: "teaser-intent-primary" },
			secondary: { base: "teaser-intent-secondary" },
			light: { base: "teaser-intent-light" },
			dark: { base: "teaser-intent-dark" },
			white: { base: "teaser-intent-white" },
			black: { base: "teaser-intent-black" },
			success: { base: "teaser-intent-success" },
			warning: { base: "teaser-intent-warning" },
			danger: { base: "teaser-intent-danger" },
			info: { base: "teaser-intent-info" },
			none: { base: "teaser-intent-none" },
		},
		orientation: {
			"orientation-vertical": {
				base: "",
			},
			"orientation-horizontal": {
				base: "teaser-orientation-horizontal",
			},
		},
		readWidthLength: {
			narrow: { base: "" },
			normal: { base: "" },
			wide: { base: "" },
		},
		readWidthAlignment: {
			start: { base: "" },
			center: { base: "" },
			end: { base: "" },
		},
		animationScrollType: {
			none: { base: "" },
			"anim-view-fadeIn-moveUp": { base: "anim-view-fadeIn-moveUp" },
			"anim-view-fadeIn-moveDown": { base: "anim-view-fadeIn-moveDown" },
			"anim-view-fadeIn-moveLeft": { base: "anim-view-fadeIn-moveLeft" },
		},
		alignmentSelfVertical: {
			stretch: { base: "" },
			start: { base: "self-start" },
			center: { base: "self-center" },
			end: { base: "self-end" },
		},
	},
	defaultVariants: {
		intent: "none",
	},
});

export function Teaser({
	inlineEditing = false,
	inlineElementKey,
	orientation = "orientation-vertical",
	heading,
	headingLabel,
	headingLevel = 2,
	headingAppearance,
	body,
	imageSrc,
	imageAlt = "",
	imageCaption,
	imageInBackground = false,
	imageInset = false,
	imagePositionEnd = false,
	iconSvgId,
	iconColor,
	iconPositionTop = false,
	iconAlignment = "start",
	link,
	intentColor = "none",
	typeVariant = "solid",
	contentAlignment = "start",
	animationHoverType = [],
	animationScrollType = "none",
	animationEffectType = "none",
	readWidthToggle = false,
	readWidthLength = "normal",
	readWidthAlignment = "center",
	aspectRatio,
	id,
	className,
}: TeaserProps) {
	const isHorizontal = orientation === "orientation-horizontal";

	const { base } = teaser({
		variant: typeVariant,
		intent: intentColor,
		contentAlignment: toTeaserAlignment(contentAlignment),
		orientation,
		iconAlignment: toTeaserAlignment(iconAlignment),
		animationScrollType,
	});

	const imageAspectRatio = toAspectRatioValue(aspectRatio);
	const hoverItemClasses = toHoverItemCssClasses(animationHoverType);
	const hoverMediaClasses = toHoverMediaCssClasses(animationHoverType);
	const resolvedIconSpriteId = iconSvgId
		? iconSvgId.replace("/umbrella/ui/icons/", "").replace(".svg", "")
		: undefined;

	const animationEffect = useAnimationEffect({
		animationEffectType,
		intentColor,
	});
	const readWidthLengthClass = readWidthToggle
		? toReadWidthLengthClass(readWidthLength)
		: undefined;
	const readWidthAlignmentClass = readWidthToggle
		? toReadWidthAlignmentClass(readWidthAlignment)
		: undefined;

	const figure = imageSrc ? (
		<figure
			className="figure"
			style={imageAspectRatio ? { aspectRatio: imageAspectRatio } : undefined}
		>
			<img
				src={imageSrc}
				alt={getAltTextFromString(imageAlt ?? undefined)}
				className={cn(
					"teaser-img w-full figure-img object-fit-cover",
					imageInBackground && "absolute inset-0 h-full",
					isHorizontal && "h-full",
				)}
			/>
			{imageCaption && (
				<figcaption className="figure-caption">{imageCaption}</figcaption>
			)}
		</figure>
	) : null;

	const contentSection = (
		<div
			className={cn(
				"teaser-content",
				readWidthToggle && "read-width",
				readWidthLengthClass,
				readWidthAlignmentClass,
			)}
		>
			{iconPositionTop && resolvedIconSpriteId && (
				<svg
					className={cn("icon teaser-icon", iconColor && `text-${iconColor}`)}
					aria-hidden="true"
				>
					<use
						href={resolveIconHref(resolvedIconSpriteId)}
					/>
				</svg>
			)}
			{(heading || headingLabel) && (
				<header>
					{headingLabel && (
						<span
							className="kicker"
							data-inline-field={inlineEditing ? "headingLabel" : undefined}
						>
							{headingLabel}
						</span>
					)}
					{heading && (
						<Heading
							data-inline-field={inlineEditing ? "heading" : undefined}
							level={headingLevel}
							display={headingAppearance}
							className="teaser-title"
						>
							{heading}
						</Heading>
					)}
				</header>
			)}
			{body && (
				<div
					className="teaser-body rich-text"
					data-inline-field={inlineEditing ? "body" : undefined}
					data-inline-field-kind={inlineEditing ? "rich-text" : undefined}
					// biome-ignore lint/security/noDangerouslySetInnerHtml: Umbraco rich text is rendered to match the Razor partial.
					dangerouslySetInnerHTML={{ __html: body }}
				/>
			)}
			{!iconPositionTop && resolvedIconSpriteId && (
				<svg
					className={cn("icon teaser-icon", iconColor && `text-${iconColor}`)}
					aria-hidden="true"
				>
					<use
						href={resolveIconHref(resolvedIconSpriteId)}
					/>
				</svg>
			)}
		</div>
	);

	return (
		<div
			id={id}
			data-inline-element-key={inlineElementKey}
			style={animationEffect.style}
			data-anim-effect={animationEffect["data-anim-effect"]}
			onPointerEnter={animationEffect.onPointerEnter}
			onPointerLeave={animationEffect.onPointerLeave}
			onPointerMove={animationEffect.onPointerMove}
			className={cn(
				base(),
				animationEffect.className,
				hoverItemClasses,
				hoverMediaClasses,
				imageSrc && "teaser-has-img",
				imageInBackground && "teaser-img-bg",
				imageInset && "teaser-img-inset",
				imagePositionEnd && "teaser-img-end",
				className,
			)}
		>
			{isHorizontal ? (
				<>
					{!imagePositionEnd && figure}
					{contentSection}
					{imagePositionEnd && figure}
				</>
			) : (
				<>
					{figure}
					{contentSection}
				</>
			)}
			{link?.href.trim() && (
				// biome-ignore lint/a11y/useAnchorContent: Matches the Razor stretched-link structure; aria-label provides the accessible name.
				<a
					href={link.href}
					target={link.target}
					{...getLinkAttributes(link)}
					aria-label={heading ?? link.label}
					className="stretched-link"
				/>
			)}
		</div>
	);
}
